import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { tenantService, authService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { FormGroup, Input, Select, PageHeader, Loading } from '../components/UI';

const PLAN_LABELS = { basic: 'Básico', professional: 'Profissional', enterprise: 'Enterprise' };

export default function SettingsPage() {
  const { user } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  // Password change
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await tenantService.getMe();
        setTenant(data);
        setForm({
          company_name: data.company_name || '',
          cnpj: data.cnpj || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          city: data.city || '',
          state: data.state || '',
          zip_code: data.zip_code || '',
        });
      } catch {
        toast.error('Erro ao carregar configurações');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await tenantService.update(form);
      toast.success('Configurações salvas');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handlePwChange = (e) => {
    const { name, value } = e.target;
    setPwForm(f => ({ ...f, [name]: value }));
  };

  const handlePwSubmit = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      return toast.error('As senhas não coincidem');
    }
    if (pwForm.new_password.length < 6) {
      return toast.error('A nova senha deve ter pelo menos 6 caracteres');
    }
    try {
      setPwSaving(true);
      await authService.changePassword({
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success('Senha alterada com sucesso');
      setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="page">
      <PageHeader
        title="Configurações"
        subtitle="Dados da empresa e preferências do sistema"
      />

      {/* Company info */}
      <div className="settings-section card">
        <h3 className="settings-title">Dados da Empresa</h3>

        {tenant && (
          <div className="tenant-meta">
            <span className="tenant-plan-badge">
              Plano: {PLAN_LABELS[tenant.plan] || tenant.plan}
            </span>
            <span className="tenant-slug">Slug: <code>{tenant.slug}</code></span>
          </div>
        )}

        <form onSubmit={handleSave}>
          <div className="form-grid-2">
            <FormGroup label="Razão Social" required>
              <Input name="company_name" value={form.company_name} onChange={handleChange} required />
            </FormGroup>
            <FormGroup label="CNPJ">
              <Input name="cnpj" value={form.cnpj} onChange={handleChange} placeholder="00.000.000/0000-00" />
            </FormGroup>
            <FormGroup label="E-mail comercial">
              <Input name="email" type="email" value={form.email} onChange={handleChange} />
            </FormGroup>
            <FormGroup label="Telefone">
              <Input name="phone" value={form.phone} onChange={handleChange} placeholder="(00) 00000-0000" />
            </FormGroup>
          </div>

          <FormGroup label="Endereço">
            <Input name="address" value={form.address} onChange={handleChange} />
          </FormGroup>

          <div className="form-grid-3">
            <FormGroup label="Cidade">
              <Input name="city" value={form.city} onChange={handleChange} />
            </FormGroup>
            <FormGroup label="Estado">
              <Select name="state" value={form.state} onChange={handleChange}>
                <option value="">Selecione</option>
                {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
                  'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup label="CEP">
              <Input name="zip_code" value={form.zip_code} onChange={handleChange} placeholder="00000-000" />
            </FormGroup>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>

      {/* Change password */}
      <div className="settings-section card" style={{ marginTop: '1.5rem' }}>
        <h3 className="settings-title">Alterar Senha</h3>
        <p className="settings-description">
          Você está logado como <strong>{user?.name}</strong> ({user?.email})
        </p>

        <form onSubmit={handlePwSubmit}>
          <div className="form-grid-2">
            <FormGroup label="Senha atual" required>
              <Input
                name="current_password"
                type="password"
                value={pwForm.current_password}
                onChange={handlePwChange}
                required
              />
            </FormGroup>
          </div>
          <div className="form-grid-2">
            <FormGroup label="Nova senha" required>
              <Input
                name="new_password"
                type="password"
                value={pwForm.new_password}
                onChange={handlePwChange}
                required
              />
            </FormGroup>
            <FormGroup label="Confirmar nova senha" required>
              <Input
                name="confirm"
                type="password"
                value={pwForm.confirm}
                onChange={handlePwChange}
                required
              />
            </FormGroup>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={pwSaving}>
              {pwSaving ? 'Alterando...' : 'Alterar Senha'}
            </button>
          </div>
        </form>
      </div>

      {/* System info */}
      <div className="settings-section card" style={{ marginTop: '1.5rem' }}>
        <h3 className="settings-title">Informações do Sistema</h3>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Versão</span>
            <span className="info-value">SaniTech 1.0.0</span>
          </div>
          <div className="info-item">
            <span className="info-label">Stack</span>
            <span className="info-value">FastAPI + React + PostgreSQL</span>
          </div>
          <div className="info-item">
            <span className="info-label">Suporte</span>
            <span className="info-value">suporte@sanitech.com.br</span>
          </div>
        </div>
      </div>

      {/* Backup */}
      <div className="settings-section card" style={{ marginTop: '1.5rem' }}>
        <h3 className="settings-title">Backup de Dados</h3>
        <p className="settings-description">
          Exporte todos os seus dados em formato JSON para guardar em local seguro.
          Recomendamos realizar backup semanalmente.
        </p>
        <BackupSection />
      </div>
    </div>
  );
}

function BackupSection() {
  const [info, setInfo] = React.useState(null);
  const [downloading, setDownloading] = React.useState(false);

  React.useEffect(() => {
    fetch('/api/backup/info', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => r.json()).then(setInfo).catch(() => {});
  }, []);

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch('/api/backup/tenant/export', { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('Erro ao gerar backup');
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `sanitech_backup_${new Date().toISOString().slice(0,10)}.zip`;
      a.click();
      toast.success('Backup baixado com sucesso!');
    } catch { toast.error('Erro ao gerar backup'); }
    finally { setDownloading(false); }
  };

  return (
    <div>
      {info && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.25rem' }}>
          {Object.entries(info.counts).map(([key, count]) => (
            <div key={key} style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: '.75rem', textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>{count}</div>
              <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', textTransform: 'capitalize' }}>{key}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <button className="btn btn-primary" onClick={downloadBackup} disabled={downloading}>
          {downloading ? '⏳ Gerando...' : '💾 Exportar Dados (ZIP + JSON)'}
        </button>
        <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>
          Inclui: clientes, ativos, contratos, logística, financeiro e notas fiscais
        </span>
      </div>
    </div>
  );
}
